/**
 * Pendaftar PPDB.
 *
 * Pendaftar masuk dari dua kanal: form di situs abduljalal.org (source=WEBSITE)
 * dan input petugas (source=MANUAL/WHATSAPP). Halaman ini dipakai panitia untuk
 * menindaklanjuti (dihubungi, verifikasi) dan menetapkan keputusan.
 */
import { useState } from 'react'
import {
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  StatCard,
  Textarea,
  type KolomTabel,
} from '@/components'
import { api } from '@/lib/api'
import { formatTanggal } from '@/lib/format'
import { useAksi, useApi, usePaged } from '@/lib/useApi'

interface Pendaftar {
  id: string
  registrationNumber: string
  applicantName: string
  gender: string | null
  birthPlace: string | null
  birthDate: string | null
  schoolOrigin: string | null
  guardianName: string | null
  guardianPhone: string | null
  email: string | null
  address: string | null
  notes: string | null
  selectionNotes: string | null
  status: string
  stage: string
  source: string
  rejectionReason: string | null
  selectionScore: string | null
  createdAt: string
  unit: { code: string; name: string }
}

interface Statistik {
  perStatus: Record<string, number>
  perSumber: Record<string, number>
  total: number
}

const STATUS = [
  { value: 'BARU', label: 'Baru' },
  { value: 'DIHUBUNGI', label: 'Sudah dihubungi' },
  { value: 'VERIFIKASI', label: 'Verifikasi berkas' },
  { value: 'CADANGAN', label: 'Cadangan' },
  { value: 'DITERIMA', label: 'Diterima' },
  { value: 'DITOLAK', label: 'Ditolak' },
]

const NADA: Record<string, 'netral' | 'peringatan' | 'sukses' | 'bahaya' | 'info'> = {
  BARU: 'info',
  DIHUBUNGI: 'peringatan',
  VERIFIKASI: 'peringatan',
  CADANGAN: 'info',
  DITERIMA: 'sukses',
  DITOLAK: 'bahaya',
}

const labelStatus = (s: string) => STATUS.find((x) => x.value === s)?.label ?? s

/** Nomor HP dinormalkan ke format wa.me (62…). */
function tautanWhatsapp(nomor: string | null): string | null {
  if (!nomor) return null
  const angka = nomor.replace(/[^0-9]/g, '')
  if (angka.length < 8) return null
  const internasional = angka.startsWith('0') ? `62${angka.slice(1)}` : angka.startsWith('62') ? angka : `62${angka}`
  return `https://wa.me/${internasional}`
}

export default function PendaftarPPDB() {
  const aksi = useAksi()
  const [cari, setCari] = useState('')
  const [status, setStatus] = useState('')
  const [detail, setDetail] = useState<Pendaftar | null>(null)
  const [galat, setGalat] = useState<string | null>(null)
  const [tindakLanjut, setTindakLanjut] = useState({ status: 'DIHUBUNGI', catatan: '' })
  const [keputusan, setKeputusan] = useState({ keputusan: 'TERIMA', nilai: '', alasan: '' })

  const statistik = useApi<Statistik>('/admissions/stats')
  const daftar = usePaged<Pendaftar>('/admissions', {
    query: { status: status || undefined, q: cari.trim() || undefined },
  })

  const bukaDetail = (p: Pendaftar) => {
    setDetail(p)
    setGalat(null)
    setTindakLanjut({
      status: ['BARU', 'DIHUBUNGI', 'VERIFIKASI', 'CADANGAN'].includes(p.status) ? p.status : 'DIHUBUNGI',
      catatan: p.selectionNotes ?? '',
    })
    setKeputusan({ keputusan: 'TERIMA', nilai: '', alasan: '' })
  }

  const simpanTindakLanjut = async () => {
    if (!detail) return
    setGalat(null)
    const hasil = await aksi.jalankan(
      () =>
        api.patch(`/admissions/${detail.id}`, {
          status: tindakLanjut.status,
          selectionNotes: tindakLanjut.catatan.trim() || undefined,
        }),
      {
        sukses: 'Tindak lanjut disimpan.',
        setelah: () => {
          setDetail(null)
          daftar.muat()
          statistik.muat()
        },
      },
    )
    if (!hasil) setGalat(aksi.galat)
  }

  const tetapkan = async () => {
    if (!detail) return
    setGalat(null)
    const hasil = await aksi.jalankan(
      () =>
        api.post(`/admissions/${detail.id}/decision`, {
          keputusan: keputusan.keputusan,
          nilaiSeleksi: keputusan.nilai === '' ? undefined : Number(keputusan.nilai),
          alasan: keputusan.alasan.trim() || undefined,
        }),
      {
        sukses: keputusan.keputusan === 'TERIMA' ? 'Pendaftar diterima.' : 'Pendaftar ditolak.',
        setelah: () => {
          setDetail(null)
          daftar.muat()
          statistik.muat()
        },
      },
    )
    if (!hasil) setGalat(aksi.galat)
  }

  const kolom: KolomTabel<Pendaftar>[] = [
    {
      kunci: 'applicantName',
      judul: 'Calon peserta didik',
      render: (b) => (
        <div>
          <p className="font-medium text-netral-800">{b.applicantName}</p>
          <p className="font-mono text-xs text-netral-500">{b.registrationNumber}</p>
        </div>
      ),
    },
    {
      kunci: 'unit',
      judul: 'Unit',
      render: (b) => (
        <div>
          <p className="text-netral-800">{b.unit.name}</p>
          <p className="text-xs text-netral-500">{b.unit.code}</p>
        </div>
      ),
    },
    { kunci: 'schoolOrigin', judul: 'Asal sekolah', render: (b) => b.schoolOrigin ?? '—' },
    {
      kunci: 'guardianPhone',
      judul: 'Kontak wali',
      render: (b) => {
        const wa = tautanWhatsapp(b.guardianPhone)
        return (
          <div>
            <p className="text-netral-800">{b.guardianPhone ?? '—'}</p>
            {wa ? (
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-medium text-yayasan-700 hover:underline"
              >
                Chat WhatsApp
              </a>
            ) : null}
          </div>
        )
      },
    },
    {
      kunci: 'source',
      judul: 'Sumber',
      render: (b) => <Badge nada={b.source === 'WEBSITE' ? 'info' : 'netral'}>{b.source}</Badge>,
    },
    {
      kunci: 'status',
      judul: 'Status',
      render: (b) => <Badge nada={NADA[b.status] ?? 'netral'}>{labelStatus(b.status)}</Badge>,
    },
    { kunci: 'createdAt', judul: 'Masuk', render: (b) => formatTanggal(b.createdAt) },
    {
      kunci: 'aksi',
      judul: '',
      sortable: false,
      render: (b) => (
        <Button ukuran="kecil" varian="garis" onClick={() => bukaDetail(b)}>
          Tindak lanjut
        </Button>
      ),
    },
  ]

  const s = statistik.data
  const sudahDiputuskan = detail ? ['DITERIMA', 'DITOLAK'].includes(detail.status) : false

  return (
    <div>
      <PageHeader
        judul="Pendaftar PPDB"
        deskripsi="Pendaftar dari form abduljalal.org dan input petugas. Data tersimpan di sistem, bukan hanya lewat WhatsApp."
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total pendaftar" nilai={s?.total ?? 0} />
        <StatCard label="Baru" nilai={s?.perStatus?.BARU ?? 0} sublabel="belum dihubungi" />
        <StatCard label="Diterima" nilai={s?.perStatus?.DITERIMA ?? 0} nada="hijau" />
        <StatCard label="Dari website" nilai={s?.perSumber?.WEBSITE ?? 0} sublabel="kanal online" />
      </div>

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Cari"
            placeholder="Nama, nomor pendaftaran, atau nomor HP"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
          />
          <Select
            label="Status"
            placeholder="Semua status"
            opsi={STATUS}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          />
        </div>
      </Card>

      <DataTable<Pendaftar>
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong="Belum ada pendaftar"
        deskripsiKosong="Pendaftar dari form PPDB di abduljalal.org akan muncul di sini otomatis."
      />

      <Modal
        terbuka={Boolean(detail)}
        onTutup={() => setDetail(null)}
        judul={detail?.applicantName ?? 'Detail pendaftar'}
        deskripsi={detail ? `${detail.registrationNumber} · ${detail.unit.name}` : undefined}
        lebar="max-w-3xl"
      >
        {detail ? (
          <div className="space-y-5">
            {galat ? (
              <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{galat}</p>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ['Jenis kelamin', detail.gender === 'L' ? 'Laki-laki' : detail.gender === 'P' ? 'Perempuan' : '—'],
                ['Tempat, tanggal lahir', [detail.birthPlace, detail.birthDate ? formatTanggal(detail.birthDate) : null].filter(Boolean).join(', ') || '—'],
                ['Asal sekolah', detail.schoolOrigin ?? '—'],
                ['Nama wali', detail.guardianName ?? '—'],
                ['Nomor wali', detail.guardianPhone ?? '—'],
                ['Email', detail.email ?? '—'],
                ['Alamat', detail.address ?? '—'],
                ['Catatan pendaftar', detail.notes ?? '—'],
              ].map(([label, nilai]) => (
                <div key={label as string}>
                  <p className="text-xs uppercase tracking-wide text-netral-500">{label}</p>
                  <p className="text-sm text-netral-800">{nilai as string}</p>
                </div>
              ))}
            </div>

            {sudahDiputuskan ? (
              <div className="rounded-lg border border-netral-200 bg-netral-50 p-3">
                <p className="text-sm font-medium text-netral-800">
                  Keputusan: {labelStatus(detail.status)}
                  {detail.selectionScore ? ` · Nilai seleksi ${detail.selectionScore}` : ''}
                </p>
                {detail.rejectionReason ? (
                  <p className="mt-1 text-sm text-netral-600">Alasan: {detail.rejectionReason}</p>
                ) : null}
              </div>
            ) : (
              <div className="space-y-5 border-t border-netral-200 pt-4">
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-netral-800">Tindak lanjut</h3>
                  <KolomForm>
                    <Select
                      label="Status"
                      opsi={STATUS.filter((x) => ['BARU', 'DIHUBUNGI', 'VERIFIKASI', 'CADANGAN'].includes(x.value))}
                      value={tindakLanjut.status}
                      onChange={(e) => setTindakLanjut({ ...tindakLanjut, status: e.target.value })}
                    />
                    <Input label="Nilai seleksi" type="number" min={0} max={100} step="0.01" value={keputusan.nilai}
                      onChange={(e) => setKeputusan({ ...keputusan, nilai: e.target.value })}
                      bantuan="Opsional, diisi saat keputusan diambil." />
                  </KolomForm>
                  <div className="mt-2">
                    <Textarea
                      label="Catatan panitia"
                      rows={2}
                      value={tindakLanjut.catatan}
                      onChange={(e) => setTindakLanjut({ ...tindakLanjut, catatan: e.target.value })}
                    />
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Button varian="garis" memuat={aksi.memuat} onClick={simpanTindakLanjut}>
                      Simpan tindak lanjut
                    </Button>
                  </div>
                </div>

                <div className="border-t border-netral-200 pt-4">
                  <h3 className="mb-2 text-sm font-semibold text-netral-800">Keputusan seleksi</h3>
                  <KolomForm>
                    <Select
                      label="Keputusan"
                      opsi={[
                        { value: 'TERIMA', label: 'Diterima' },
                        { value: 'TOLAK', label: 'Ditolak' },
                      ]}
                      value={keputusan.keputusan}
                      onChange={(e) => setKeputusan({ ...keputusan, keputusan: e.target.value })}
                    />
                    <Input
                      label="Alasan (wajib bila ditolak)"
                      value={keputusan.alasan}
                      onChange={(e) => setKeputusan({ ...keputusan, alasan: e.target.value })}
                    />
                  </KolomForm>
                  <div className="mt-3 flex justify-end">
                    <Button
                      varian={keputusan.keputusan === 'TERIMA' ? 'utama' : 'bahaya'}
                      memuat={aksi.memuat}
                      onClick={tetapkan}
                    >
                      {keputusan.keputusan === 'TERIMA' ? 'Terima pendaftar' : 'Tolak pendaftar'}
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-netral-500">
                    Keputusan bersifat final dan tercatat di audit log. Pastikan data sudah diverifikasi.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
